/**
 * LE NOYAU DU CLIENT — transport et vérification PUBLIQUE.
 *
 * ⚠️ CE FICHIER EXISTE POUR LE §17, PAS PAR GOÛT DU DÉCOUPAGE. La page publique
 * (`verifier.html`) et le poste de travail (`index.html`) sont deux paquets
 * distincts : « le citoyen ne rentre pas dans l'environnement administratif ».
 * Tant que tout vivait dans `api.ts`, le paquet PUBLIC embarquait le morceau
 * partagé — donc la liste complète des routes de l'atelier : `/mutations`,
 * `/comptes`, `/releves`, `/documents`. Aucune donnée ne fuyait, mais la carte
 * du système, si. Mesuré sur le paquet bâti avant correction.
 *
 * ⚠️ ET ON NE DUPLIQUE PAS LE TRANSPORT POUR AUTANT. Écrire un second `fetch`
 * pour la page publique recréerait deux fois la distinction entre une panne
 * réseau et une erreur applicative — et les deux copies divergeraient au
 * premier correctif appliqué à une seule. Un transport, deux surfaces.
 *
 * ⚠️ AUCUN JETON NE TRANSITE ICI. La session vit dans un cookie `httpOnly` que
 * ce code ne peut pas lire ; `credentials: 'same-origin'` suffit à le porter.
 * Sur la page publique il n'y a de toute façon aucune session : la
 * vérification est anonyme, c'est ce qui la rend utilisable.
 */

/**
 * LE MESSAGE PORTE LES PROBLÈMES DE VALIDATION — §82.
 *
 * ⚠️⚠️ MESURÉ LE 21/09/2026 : **7 écrans sur 37** lisent `details`. Les trente
 * autres affichent `e.message` seul. Le service envoie pourtant tout ce qu'il
 * faut : `analyser()` range chaque problème dans `details` avec le chemin du
 * champ, et `core/http.ts` les rend au client. C'est l'AFFICHAGE qui les perd.
 *
 * Le cas qui l'a révélé : un dépôt de mutation portant une date d'effet
 * future. Le serveur répond « valideDu : la date d'effet d'un droit ne peut
 * pas être dans le futur : 2026-09-22 est postérieure à aujourd'hui » ; l'agent
 * lit « L'effet projeté est inexploitable ». Il ne sait ni quel champ, ni
 * quelle valeur, ni pourquoi.
 *
 * ⚠️ CORRIGÉ ICI ET NON DANS LES TRENTE ÉCRANS. Les réparer un par un aurait
 * produit trente copies de la même règle — le motif le plus coûteux de ce
 * dépôt — et le trente-et-unième écran, écrit demain, l'aurait oubliée.
 *
 * ⚠️ ON NE TOUCHE QUE LE MESSAGE, JAMAIS `details`, qui reste rendu tel quel.
 * Il n'est pas qu'un texte d'erreur : `connexion.tsx` y lit
 * `enrolement_possible` pour décider d'un parcours. Le réécrire casserait une
 * fonctionnalité en réparant un affichage.
 *
 * ⚠️ ET SEULE LA FORME ZOD EST TRAITÉE — un tableau de `{champ, probleme}`.
 * Les autres formes (`{contrainte}`, `{limite_octets}`) sont laissées
 * intactes : les aplatir dans le message afficherait des noms de contraintes
 * SQL en anglais à un agent, ce que `traduireErreurSql` prend justement soin
 * de ne pas faire.
 */
function messageAvecDetails(message: string, details: unknown): string {
  if (!Array.isArray(details)) return message;
  const points = details
    .map((d) => {
      if (!d || typeof d !== 'object') return null;
      const { champ, probleme } = d as { champ?: unknown; probleme?: unknown };
      if (typeof probleme !== 'string' || !probleme) return null;
      return typeof champ === 'string' && champ ? `${champ} : ${probleme}` : probleme;
    })
    .filter((x): x is string => x !== null);
  return points.length > 0 ? `${message} — ${points.join(' · ')}` : message;
}

export class ErreurApi extends Error {
  constructor(
    readonly statut: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    /** Corps JSON complet d'un 404 — §16 y range un RÉSULTAT de vérification. */
    readonly corps404?: Record<string, unknown>,
  ) { super(messageAvecDetails(message, details)); }

  /** Une panne d'une source AMONT n'est pas une faute de l'utilisateur. */
  get estIndisponibilite(): boolean { return this.statut === 503; }
  get estRefus(): boolean { return this.statut === 403; }
  get estNonAuthentifie(): boolean { return this.statut === 401; }
}

export async function appeler<T>(chemin: string, options: RequestInit = {}): Promise<T> {
  let reponse: Response;
  try {
    reponse = await fetch(chemin, {
      ...options,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...(options.headers ?? {}) },
    });
  } catch (err) {
    // ⚠️ Le réseau qui tombe n'est PAS une erreur applicative. Les confondre
    // afficherait « données invalides » quand le câble est débranché.
    throw new ErreurApi(0, 'RESEAU', "Le service est injoignable depuis ce poste", err);
  }

  if (reponse.status === 204) return undefined as T;

  const texte = await reponse.text();
  let corps: any = null;
  if (texte) { try { corps = JSON.parse(texte); } catch { corps = { brut: texte }; } }

  if (!reponse.ok) {
    throw new ErreurApi(
      reponse.status,
      corps?.erreur?.code ?? 'INCONNU',
      corps?.erreur?.message ?? corps?.message ?? `Erreur ${reponse.status}`,
      corps?.erreur?.details,
      reponse.status === 404 && corps && typeof corps === 'object' ? corps : undefined,
    );
  }
  return corps as T;
}

/** Un fichier servi par le registre, avec ce que ses en-têtes déclarent. */
export interface FichierRecu {
  contenu: string;
  /** Le nom voulu par le SERVICE (`Content-Disposition`), jamais inventé ici. */
  nom: string;
  /** `X-Export-Lignes` : le décompte RELU dans le fichier, pas estimé. */
  lignes: number | null;
  /** `X-Export-Tronque` : la limite a mordu, il manque des lignes. */
  tronque: boolean;
}

/**
 * RECEVOIR UN FICHIER PLUTÔT QU'UN OBJET.
 *
 * ⚠️⚠️ `appeler()` NE PEUT PAS SERVIR ICI, ET PAS POUR UNE QUESTION DE TYPE.
 * Il fait `JSON.parse` et, sur un CSV, retombe sur `{ brut: texte }` : le
 * contenu survit, mais les EN-TÊTES sont perdus. Or c'est là que vit tout ce
 * qui rend l'export honnête — `X-Export-Lignes` (le décompte relu DANS le
 * fichier) et `X-Export-Tronque` (la limite a mordu). Sans eux, l'écran
 * annoncerait le nombre de lignes qu'il a demandées, jamais celui qu'il a
 * reçu, et une troncature passerait pour un export complet.
 *
 * ⚠️ ET LE TRANSPORT RESTE UNIQUE. Recopier `fetch` + `ErreurApi` ailleurs
 * produirait deux façons de distinguer une panne réseau d'un refus applicatif,
 * et la seconde serait fausse au premier correctif. Voir l'en-tête du fichier.
 */
export async function appelerFichier(
  chemin: string, options: RequestInit = {},
): Promise<FichierRecu> {
  let reponse: Response;
  try {
    reponse = await fetch(chemin, {
      ...options,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...(options.headers ?? {}) },
    });
  } catch (err) {
    throw new ErreurApi(0, 'RESEAU', 'Le service est injoignable depuis ce poste', err);
  }

  const texte = await reponse.text();
  if (!reponse.ok) {
    /* Un refus reste du JSON : on le lit comme tel pour rendre le MOTIF, et
       non « erreur 400 » sur un corps qui nomme le champ fautif. */
    let corps: any = null;
    if (texte) { try { corps = JSON.parse(texte); } catch { corps = null; } }
    throw new ErreurApi(
      reponse.status,
      corps?.erreur?.code ?? 'INCONNU',
      corps?.erreur?.message ?? `Erreur ${reponse.status}`,
      corps?.erreur?.details);
  }

  /* `attachment; filename="parcelles-2026-09-23.csv"` — on prend ce que le
     service a décidé. Un nom fabriqué par l'écran divergerait de celui que le
     journal a consigné, et deux fichiers du même export porteraient deux noms. */
  const dispo = reponse.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const lignes = reponse.headers.get('x-export-lignes');
  return {
    contenu: texte,
    nom: m?.[1] ?? 'export.csv',
    lignes: lignes === null ? null : Number(lignes),
    tronque: (reponse.headers.get('x-export-tronque') ?? 'false') === 'true',
  };
}

/**
 * §16 — LE TRANSPORT NE DOIT PAS AVALER UN RÉSULTAT.
 *
 * ⚠️ `/verify/parcelle/:id` et `/verify/document/:jeton` rendent **404 avec un
 * corps utile** quand la référence est inconnue : `etat: 'non_verifiable'` et
 * l'explication « cela ne signifie pas que la parcelle n'existe pas, la
 * couverture cadastrale est partielle ». C'est un des QUATRE états, pas une
 * panne. `appeler()` lèverait, et l'écran afficherait une erreur technique là
 * où le citoyen doit lire une réponse — dans un contexte où l'on cherche à
 * savoir si un terrain existe, transformer « je ne sais pas » en « erreur »
 * fait conclure à une panne du service, donc à rien du tout.
 *
 * ⚠️ ET LE 404 SANS `etat` RESTE UNE ERREUR. Une route mal orthographiée rend
 * `{ erreur: { code: 'ROUTE_INCONNUE' } }` avec le même 404. L'accepter
 * afficherait « information non accessible » sur une faute de frappe du
 * développeur : le pire mensonge possible sur cette surface. Le discriminant
 * est la PRÉSENCE du champ `etat`, jamais le code HTTP.
 */
export async function appelerVerification<T extends { etat?: string }>(chemin: string): Promise<T> {
  try {
    return await appeler<T>(chemin);
  } catch (err) {
    if (err instanceof ErreurApi && err.statut === 404 && err.corps404 && 'etat' in err.corps404) {
      return err.corps404 as T;
    }
    throw err;
  }
}

/**
 * §16 — le résultat porte QUATRE états, jamais un booléen « vérifié ».
 * ⚠️ `revoque` s'ajoute pour le QR documentaire : un document révoqué n'est pas
 * « non vérifiable » (on SAIT), ni « incohérent » (rien ne se contredit) — il
 * a été retiré par son autorité émettrice, et c'est une information forte.
 */
export type EtatVerification = 'verifie' | 'declare' | 'incoherent' | 'non_verifiable' | 'revoque';

/** Un point contrôlé, avec ce qui a été OBSERVÉ — jamais un simple booléen. */
export interface PointVerifie {
  point: string; resultat: boolean; observe?: string; le?: string | null;
}

export interface Verification {
  etat: EtatVerification;
  pastille?: string;
  libelle?: string;
  identifiant?: string;
  message?: string;
  avertissement?: string;
  recommandation?: string;
  /** Ce qui a été contrôlé, point par point. §16 l'exige. */
  verifie?: PointVerifie[];
  /** Ce qu'on ne sait PAS, affiché aussi explicitement que ce qu'on sait. */
  non_accessible?: string[];
  non_verifiable?: string[];
  contexte?: Record<string, unknown>;
  document?: Record<string, unknown>;
  empreinte_sha256?: string;
  ecarts?: unknown;
  [k: string]: unknown;
}


/**
 * Les trois vérifications publiques — et RIEN d'autre.
 *
 * ⚠️ Toute route ajoutée ici part dans le paquet public. C'est le seul endroit
 * du client où cette phrase est vraie, et c'est pour cela qu'il est petit.
 */
export const verifications = {
  parcelle: (identifiant: string) =>
    appelerVerification<Verification>(`/verify/parcelle/${encodeURIComponent(identifiant)}`),
  annonce: (bienId: string) =>
    appelerVerification<Verification>(`/verify/annonce/${encodeURIComponent(bienId)}`),
  document: (jeton: string) =>
    appelerVerification<Verification>(`/verify/document/${encodeURIComponent(jeton)}`),
};
